import React, { useMemo, useState } from 'react';
import './style.scss';
import SelectableListItem from 'components/Utils/SelectableListItem';
import CalendarUtils from 'services/CalendarUtils';
import moment from 'moment-timezone';
import { DefaultSwitch as Switch } from 'services/UIUtils';
import DefaultButton from 'components/Utils/NewDefaultButton';
import ShiftBadge from 'components/Schedule/ShiftsEditor/ShiftBadge';
import { NON_WORKING_REASONS } from 'components/Schedule/ShiftsEditor/EditShift';
import DefaultSelectField from 'components/Utils/DefaultSelectField';
import StaffTimePicker from 'components/Utils/StaffTimePicker';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import StackItemsView from 'components/Common/StackItemsView';
import Icon from 'components/Utils/NewIcon';
import IconButton from '@material-ui/core/IconButton';
import ShiftUtils, { SHIFT_COLORS } from 'services/ShiftUtils';
import DefaultTextField from 'components/Utils/DefaultTextField';

const ShiftTemplatesEditor = () => {
  const dispatch = useAppDispatch();

  const templates = useAppSelector((state) => state.shifts.templates);

  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);

  async function saveTemplate(template) {
    setLoading('save');
    await dispatch(api.Shifts.addClinicShiftTemplate({ shiftTemplate: template }), {
      errorTitle: 'Shift Template',
    });
    setLoading();
  }

  async function deleteTemplate(template) {
    setLoading('delete');
    const { isSuccess } = await dispatch(
      api.Shifts.removeClinicShiftTemplate({ id: template.id }),
      {
        errorTitle: 'Shift Template',
        meta: true,
      },
    );
    if (isSuccess) setSelected({});
    setLoading();
  }

  return (
    <div className="shift-templates-editor full-height">
      <div className="full-height-panel scroll-list underside-right-panel">
        <div className="button-wrapper">
          <DefaultButton name="Add Template" onClick={() => setSelected({})} />
        </div>
        <ShiftTemplateList
          templates={templates}
          onClick={(template) => setSelected(template)}
          selected={selected}
        />
      </div>
      <div className="details-panel shift-template padded-box full-width">
        {!!selected && (
          <ShiftTemplateDetails
            key={selected.id}
            template={selected}
            onSave={saveTemplate}
            onDelete={deleteTemplate}
            loading={loading}
          />
        )}
      </div>
    </div>
  );
};

const ShiftTemplateDetails = ({ template = {}, onSave, onDelete, loading }) => {
  const [data, setData] = useState({ ...template });
  const [breakValue, setBreakValue] = useState({});

  function convertFromMilitary(military) {
    return military ? CalendarUtils.militaryTimeToUnix(moment().unix(), military) : null;
  }

  const startHour = convertFromMilitary(data.start_hour);
  const endHour = convertFromMilitary(data.end_hour);

  function onChangeTime(name, val) {
    let militaryTime = CalendarUtils.format(val, 'HHmm');
    setData({ ...data, [name]: militaryTime });
  }

  function onChangeBreakTime(name, val) {
    let militaryTime = CalendarUtils.format(val, 'HHmm');
    setBreakValue((prevState) => {
      return { ...prevState, [name]: militaryTime };
    });
  }

  function addBreak() {
    if (breakValue.start_hour && breakValue.end_hour && breakValue.name) {
      setData((prevState) => {
        return { ...prevState, breaks: [...(prevState.breaks || []), breakValue] };
      });
      setBreakValue({});
    }
  }

  function deleteBreak(data) {
    setData((prevState) => {
      return { ...prevState, breaks: prevState.breaks.filter((b) => b !== data) };
    });
  }

  const isValid = data.start_hour && data.end_hour;

  return (
    <div className="shift-template-details full-width">
      <div className="row">
        <DefaultTextField
          label="Template Name"
          style={{ width: '150px' }}
          name="name"
          value={data.name}
          onChange={(event) => setData({ ...data, name: event.target.value })}
        />
        <StaffTimePicker
          value={startHour}
          label="Start Hour"
          name="start_hour"
          onChange={(event, val) => onChangeTime('start_hour', val)}
          style={{ marginRight: '10px', width: '150px' }}
        />
        <StaffTimePicker
          value={endHour}
          label="End Hour"
          name="end_hour"
          onChange={(event, val) => onChangeTime('end_hour', val)}
          style={{ width: '150px' }}
        />
      </div>
      <div className="row" style={{ marginTop: '15px' }}>
        <div className="wrapper">
          <Switch
            label="Non-working"
            padded={false}
            checked={!!data.non_working}
            onChange={(event, isInputChecked) => {
              setData({
                ...data,
                breaks: [],
                non_working: isInputChecked,
                non_working_reason: undefined,
              });
            }}
          />
        </div>
      </div>
      {!!data.non_working && (
        <DefaultSelectField
          name="non_working_reason"
          label="Non-working Reason"
          value={data.non_working_reason || NON_WORKING_REASONS[0]}
          onChange={(event) => setData({ ...data, non_working_reason: event.target.value })}
          inputs={NON_WORKING_REASONS}
          style={{ width: '100%' }}
        />
      )}
      {!data.non_working && (
        <>
          <div className="row" style={{ marginTop: '15px' }}>
            <div className="wrapper">
              <Switch
                label="Telemedicine Only"
                padded={false}
                checked={!!data.telemedicine_only}
                onChange={(event, isInputChecked) => {
                  setData({
                    ...data,
                    telemedicine_only: isInputChecked,
                  });
                }}
              />
            </div>
          </div>
          <span className="small-title mt-5 mb-2">Breaks</span>
          <StackItemsView
            stack={data.breaks}
            onDeleteItem={deleteBreak}
            describeItem={ShiftUtils.getBreakDescription}
            hideStatus={true}
          />
          <div className="row breaks-input">
            <DefaultSelectField
              label="Break Name"
              width="250px"
              value={breakValue.name}
              onChange={(event) =>
                setBreakValue((prevState) => {
                  return { ...prevState, name: event.target.value };
                })
              }
              inputs={Object.values(ShiftUtils.getBreakReasons())}
              style={{ width: '120px' }}
            />
            <StaffTimePicker
              value={breakValue.start}
              label="Break Start Hour"
              name="start_hour"
              onChange={(event, val) => onChangeBreakTime('start_hour', val)}
              style={{ width: '150px' }}
            />
            <StaffTimePicker
              value={breakValue.end}
              label="Break End Hour"
              name="end_hour"
              onChange={(event, val) => onChangeBreakTime('end_hour', val)}
              style={{ width: '150px' }}
            />
            <IconButton
              onClick={addBreak}
              className="blue"
              size="small"
              style={{ width: '30px', height: '30px' }}
            >
              <Icon src="plus" className="white" showTooltip={true} alt="Add Break" />
            </IconButton>
          </div>
          <span className="small-title mt-5 mb-2">Colour</span>
          <div className="row colour-swatches">
            <div
              className={`colour-swatch none${!data.color ? ' selected' : ''}`}
              title="Automatic (no fixed colour)"
              onClick={() => setData({ ...data, color: undefined })}
            />
            {SHIFT_COLORS.map((color) => (
              <div
                key={color}
                className={`colour-swatch${data.color === color ? ' selected' : ''}`}
                style={{ background: color }}
                title={color}
                onClick={() => setData({ ...data, color })}
              />
            ))}
          </div>
        </>
      )}
      <div className="button-wrapper row">
        {!!data.id && (
          <DefaultButton
            name="Delete Template"
            icon="trash"
            className="red"
            style={{ marginTop: '25px', marginRight: '20px', width: '160px' }}
            onClick={() => onDelete(data)}
            loading={loading === 'delete'}
          />
        )}
        <DefaultButton
          name={data.id ? 'Save Changes' : 'Create Template'}
          icon="floppy-disk"
          className="blue"
          style={{ marginTop: '25px', width: '160px' }}
          onClick={() => onSave(data)}
          disabled={!isValid}
          loading={loading === 'save'}
        />
      </div>
    </div>
  );
};

export const ShiftTemplateList = ({ templates = [], onClick, selected = {} }) => {
  const orderedTemplates = useMemo(() => {
    const orderedTemplates = [...templates];
    orderedTemplates.sort((a, b) => {
      return (a.name || '').localeCompare(b.name || '');
    });
    return orderedTemplates;
  }, [templates]);

  return (
    <div className="shift-template-list">
      {orderedTemplates.map((template) => (
        <ShiftTemplateBadge
          key={template.id}
          template={template}
          selected={selected.id === template.id}
          onClick={() => onClick(template)}
        />
      ))}
    </div>
  );
};

export const ShiftTemplateBadge = ({ template, selected, onClick }) => {
  return (
    <SelectableListItem
      key={template.id}
      className="list-item"
      selected={selected}
      onClick={onClick}
      item={<ShiftBadge template={template} />}
    />
  );
};

export default ShiftTemplatesEditor;
